import {
    Alert,
    Anchor,
    Button,
    Group,
    Modal,
    SegmentedControl,
    Select,
    Stack,
    Text,
} from '@mantine/core'
import {notifications} from '@mantine/notifications'
import {addDays, format} from 'date-fns'
import {useState} from 'react'

import {businessCalendarDate, formatBusinessTimestamp} from 'src/api/business-time'
import {ApiError} from 'src/api/errors'
import type {Publication, PublishPreview, ScheduleSummary} from 'src/dispatch/availability/schemas'
import {
    usePreviewSchedule,
    usePublications,
    usePublishSchedule,
    useSendSummary,
    useSummaries,
} from 'src/dispatch/availability/use-availability'
import type {DispatchYard} from 'src/dispatch/schemas'
import {toApiDateString} from 'src/jobs/job-schedule-format'
import {alertToneColor} from 'src/theme/semantic-colors'

type PublishScheduleModalProps = {
    opened: boolean
    onClose: () => void
    yards: DispatchYard[]
    /** Null renders a yard picker. */
    yardId: string | null
    defaultYardId?: string | null
    canSendSummary?: boolean
}

type Mode = 'yard' | 'summary'

function formatPublishedLine(publication: Publication): string {
    const at = formatBusinessTimestamp(publication.publishedAt, 'h:mm a')
    return `${publication.yardName} v${publication.version} · ${publication.publishedBy} · ${at}`
}

function formatSummarySentLine(summary: ScheduleSummary): string {
    const at = formatBusinessTimestamp(summary.sentAt, 'h:mm a')
    return `Summary v${summary.version} · ${summary.sentBy} · ${at}`
}

function formatEmailedLine(
    result: Pick<Publication, 'sentTo' | 'emailCapturedOnly' | 'emailFailed'>,
    retry: string,
): string {
    const {sentTo} = result
    if (!sentTo) return 'Saved, not emailed. No company email address set'
    if (result.emailFailed) return `Could not send email to ${sentTo}. ${retry}`

    return result.emailCapturedOnly
        ? `Email delivery is off. Not sent to ${sentTo}`
        : `Emailed to ${sentTo}.`
}

type SummarySectionProps = {
    day: string
    dayLabel: string
    longDayLabel: string
    onClose: () => void
}

function AllYardsSummarySection({day, dayLabel, longDayLabel, onClose}: SummarySectionProps) {
    const {data: summaries = []} = useSummaries(day, {enabled: true})
    const send = useSendSummary()
    const [confirming, setConfirming] = useState(false)
    const [result, setResult] = useState<ScheduleSummary | null>(null)

    async function handleSend() {
        if (send.isPending) return
        try {
            setResult(await send.mutateAsync({boardDate: day}))
            setConfirming(false)
        } catch (error) {
            notifications.show({
                title: 'Could not send the summary',
                message: error instanceof Error ? error.message : 'Try again.',
                color: alertToneColor('danger'),
            })
        }
    }

    return (
        <>
            {summaries.length > 0 ? (
                <Stack gap={2}>
                    <Text size="sm" fw={600}>
                        Summary already sent for {dayLabel}
                    </Text>
                    {summaries.map((summary) => (
                        <Text key={summary.id} size="sm">
                            {formatSummarySentLine(summary)} ·{' '}
                            <Anchor href={summary.emailUrl} target="_blank">
                                view email
                            </Anchor>
                        </Text>
                    ))}
                </Stack>
            ) : (
                <Text size="sm" c="dimmed">
                    No summary sent for {dayLabel} yet.
                </Text>
            )}

            {result ? (
                <Alert
                    color={result.emailFailed ? alertToneColor('warning') : 'green'}
                    variant="light"
                    title={`${result.emailFailed ? 'Saved' : 'Sent'} all-yards summary v${result.version}`}
                >
                    <Stack gap={4}>
                        {result.repeated ? (
                            <Text size="sm">No changes since the last send</Text>
                        ) : null}
                        {result.notPublished.length > 0 ? (
                            <Text size="sm">
                                Not published yet: {result.notPublished.join(', ')}
                            </Text>
                        ) : null}
                        <Text size="sm">{formatEmailedLine(result, 'Send again to retry.')}</Text>
                        <Anchor href={result.emailUrl} target="_blank" size="sm">
                            View the email
                        </Anchor>
                    </Stack>
                </Alert>
            ) : null}

            {confirming && !result ? (
                <Alert color={alertToneColor('warning')} variant="light">
                    <Text size="sm">
                        Send the all-yards summary for {longDayLabel} to management?
                    </Text>
                </Alert>
            ) : null}

            <Group justify="flex-end">
                {result ? (
                    <Button onClick={onClose}>Done</Button>
                ) : confirming ? (
                    <>
                        <Button
                            variant="default"
                            disabled={send.isPending}
                            onClick={() => setConfirming(false)}
                        >
                            Back
                        </Button>
                        <Button
                            loading={send.isPending}
                            disabled={send.isPending}
                            onClick={() => void handleSend()}
                        >
                            Yes, send
                        </Button>
                    </>
                ) : (
                    <>
                        <Button variant="default" onClick={onClose}>
                            Close
                        </Button>
                        <Button onClick={() => setConfirming(true)}>
                            Send all-yards summary for {dayLabel}
                        </Button>
                    </>
                )}
            </Group>
        </>
    )
}

function SchedulePreview({preview}: {preview: PublishPreview}) {
    const earlier = preview.version - 1

    return (
        <Stack gap="xs">
            <Alert color={alertToneColor('info')} variant="light" title="Preview: not sent yet">
                <Stack gap={4}>
                    <Text size="sm">
                        {preview.sentTo
                            ? `This is the email that will go to ${preview.sentTo} when you publish.`
                            : 'No company email address is set, so publishing will save this but not email it.'}
                    </Text>
                    <Text size="sm" fw={600}>
                        {preview.subject}
                    </Text>
                    {preview.unchanged ? (
                        <Text size="sm">Nothing has changed since v{earlier} was published.</Text>
                    ) : earlier > 0 && preview.changes.length > 0 ? (
                        <>
                            <Text size="sm">Changes since v{earlier}:</Text>
                            {preview.changes.map((change) => (
                                <Text key={change} size="sm">
                                    · {change}
                                </Text>
                            ))}
                        </>
                    ) : null}
                </Stack>
            </Alert>
            <iframe
                title="Schedule email preview"
                srcDoc={preview.html}
                sandbox=""
                style={{
                    width: '100%',
                    height: '60vh',
                    border: '1px solid var(--mantine-color-default-border)',
                    borderRadius: 'var(--mantine-radius-sm)',
                    background: 'white',
                }}
            />
        </Stack>
    )
}

function resolveInitialYardId(
    yards: DispatchYard[],
    yardId: string | null,
    defaultYardId: string | null | undefined,
): string | null {
    if (yardId != null) return yardId
    if (defaultYardId != null && yards.some((yard) => yard.id === defaultYardId)) {
        return defaultYardId
    }

    return yards.length === 1 ? (yards[0]?.id ?? null) : null
}

/** Modal for publishing a yard schedule or sending the all-yards summary. */
export function PublishScheduleModal({opened, onClose, ...props}: PublishScheduleModalProps) {
    return (
        <Modal opened={opened} onClose={onClose} title="Publish schedule" size="xl">
            <PublishScheduleContent onClose={onClose} {...props} />
        </Modal>
    )
}

function PublishScheduleContent({
    onClose,
    yards,
    yardId,
    defaultYardId,
    canSendSummary = false,
}: Omit<PublishScheduleModalProps, 'opened'>) {
    const today = businessCalendarDate()
    const [dayChoice, setDayChoice] = useState<'today' | 'tomorrow'>('tomorrow')
    const [chosenYard, setChosenYard] = useState<string | null>(() =>
        resolveInitialYardId(yards, yardId, defaultYardId),
    )
    const [preview, setPreview] = useState<PublishPreview | null>(null)
    const [result, setResult] = useState<Publication | null>(null)
    const [mode, setMode] = useState<Mode>('yard')
    const summaryMode = canSendSummary && yardId == null && mode === 'summary'
    const boardDay = dayChoice === 'today' ? today : addDays(today, 1)
    const day = toApiDateString(boardDay)
    const dayLabel = format(boardDay, 'EEE MMM d')
    const effectiveYard = yardId ?? chosenYard
    const yardName = yards.find((yard) => yard.id === effectiveYard)?.name
    const {data: publications = []} = usePublications(day)
    const publish = usePublishSchedule()
    const previewing = usePreviewSchedule()
    const busy = publish.isPending || previewing.isPending

    async function handlePreview() {
        if (!effectiveYard || previewing.isPending) return
        try {
            setPreview(
                await previewing.mutateAsync({dispatchYardId: effectiveYard, boardDate: day}),
            )
        } catch (error) {
            notifications.show({
                title: 'Could not preview',
                message: error instanceof Error ? error.message : 'Try again.',
                color: alertToneColor('danger'),
            })
        }
    }

    async function handlePublish() {
        if (!effectiveYard || !preview || publish.isPending) return
        try {
            setResult(
                await publish.mutateAsync({
                    dispatchYardId: effectiveYard,
                    boardDate: day,
                    previewFingerprint: preview.fingerprint,
                }),
            )
            setPreview(null)
        } catch (error) {
            const changed = error instanceof ApiError && error.status === 409
            notifications.show({
                title: changed ? 'The schedule changed' : 'Could not publish',
                message: changed
                    ? 'Someone changed the board after your preview. Here is the new preview. Check it, then publish.'
                    : error instanceof Error
                      ? error.message
                      : 'Try again.',
                color: alertToneColor(changed ? 'warning' : 'danger'),
            })
            if (changed) await handlePreview()
        }
    }

    function resetPreview() {
        setPreview(null)
        setResult(null)
    }

    return (
        <Stack gap="sm">
            <Group gap="sm" wrap="wrap">
                <SegmentedControl
                    aria-label="Publish today or tomorrow"
                    value={dayChoice}
                    onChange={(value) => {
                        setDayChoice(value as 'today' | 'tomorrow')
                        resetPreview()
                    }}
                    disabled={busy}
                    data={[
                        {value: 'today', label: 'Today'},
                        {value: 'tomorrow', label: 'Tomorrow'},
                    ]}
                />
                {canSendSummary && yardId == null ? (
                    <SegmentedControl
                        aria-label="Publish a yard or send the all-yards summary"
                        value={mode}
                        onChange={(value) => {
                            setMode(value as Mode)
                            resetPreview()
                        }}
                        disabled={busy}
                        data={[
                            {value: 'yard', label: 'One yard'},
                            {value: 'summary', label: 'All yards summary'},
                        ]}
                    />
                ) : null}
                {yardId == null && !summaryMode ? (
                    <Select
                        aria-label="Yard to publish"
                        placeholder="Which yard?"
                        data={yards.map((yard) => ({value: yard.id, label: yard.name}))}
                        value={chosenYard}
                        onChange={(value) => {
                            setChosenYard(value)
                            resetPreview()
                        }}
                        disabled={busy}
                        w={180}
                    />
                ) : null}
            </Group>

            {summaryMode ? (
                <AllYardsSummarySection
                    key={day}
                    day={day}
                    dayLabel={dayLabel}
                    longDayLabel={format(boardDay, 'EEEE MMM d')}
                    onClose={onClose}
                />
            ) : (
                <>
                    {publications.length > 0 ? (
                        <Stack gap={2}>
                            <Text size="sm" fw={600}>
                                Already published for {dayLabel}
                            </Text>
                            {publications.map((publication) => (
                                <Text key={publication.id} size="sm">
                                    {formatPublishedLine(publication)} ·{' '}
                                    <Anchor href={publication.emailUrl} target="_blank">
                                        view email
                                    </Anchor>
                                </Text>
                            ))}
                        </Stack>
                    ) : (
                        <Text size="sm" c="dimmed">
                            Nothing published for {dayLabel} yet.
                        </Text>
                    )}

                    {result ? (
                        <Alert
                            color={result.emailFailed ? alertToneColor('warning') : 'green'}
                            variant="light"
                            title={`Published ${result.yardName} v${result.version}`}
                        >
                            <Stack gap={4}>
                                {result.repeated ? (
                                    <Text size="sm">No changes since the last publish</Text>
                                ) : null}
                                {result.version > 1 ? (
                                    result.changes.length > 0 ? (
                                        <>
                                            <Text size="sm">Changes in this update:</Text>
                                            {result.changes.map((change) => (
                                                <Text key={change} size="sm">
                                                    · {change}
                                                </Text>
                                            ))}
                                        </>
                                    ) : (
                                        <Text size="sm">No changes since the last version.</Text>
                                    )
                                ) : null}
                                <Text size="sm">
                                    {formatEmailedLine(result, 'Publish again to retry.')}
                                </Text>
                                <Anchor href={result.emailUrl} target="_blank" size="sm">
                                    View the email
                                </Anchor>
                            </Stack>
                        </Alert>
                    ) : null}

                    {preview && !result ? <SchedulePreview preview={preview} /> : null}

                    <Group justify="flex-end">
                        {result ? (
                            <Button onClick={onClose}>Done</Button>
                        ) : preview ? (
                            <>
                                <Button
                                    variant="default"
                                    disabled={busy}
                                    onClick={() => setPreview(null)}
                                >
                                    Back
                                </Button>
                                <Button
                                    loading={publish.isPending}
                                    disabled={busy}
                                    onClick={() => void handlePublish()}
                                >
                                    Publish {preview.yardName} for {dayLabel}
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button variant="default" onClick={onClose}>
                                    Close
                                </Button>
                                <Button
                                    loading={previewing.isPending}
                                    disabled={!effectiveYard || busy}
                                    onClick={() => void handlePreview()}
                                >
                                    {yardName ? `Preview ${yardName} for ${dayLabel}` : 'Preview'}
                                </Button>
                            </>
                        )}
                    </Group>
                </>
            )}
        </Stack>
    )
}
